import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { OrbFan } from '@/kit';
import { ROUTES } from '@/config/routes';
import { useHottieChat } from '@/modules/hottie/useHottieChat';
import { openInKitHottieSheet } from '@/modules/hottie/open';

/**
 * The Orb — the one persistent control on every screen (Screen System §1).
 *
 *   Tap the Orb   -> the route fan / rail submenu in place
 *   Hold the Orb  -> in-kit HottieSheet (Chat + Voice)
 *
 * Do not pass `onHottie`. That override skips the kit sheet and was how hold
 * hopped to `/hottie` or opened a second dock. The kit default is the sheet.
 * Desktop tap is never Hottie.
 *
 * Routes the fan will not show: auth, onboarding and legal. §6 says ONBOARDING
 * is the one place the chassis is deliberately incomplete — navigation does not
 * exist until you are in, and the Orb's first appearance is the moment the
 * product starts.
 */

const PATHS: Record<string, string> = {
  feed: ROUTES.feed,
  people: ROUTES.grid,
  places: ROUTES.map,
  messages: ROUTES.messages,
  passport: ROUTES.settings,
};

/** Surfaces that exist before you are "in", where the Orb must not appear. */
const SUPPRESSED: ReadonlySet<string> = new Set<string>([
  ROUTES.landing,
  ROUTES.login,
  ROUTES.signup,
  ROUTES.profileBuilder,
  ROUTES.verify,
  ROUTES.verifyAge,
]);

function activeRouteFor(pathname: string): string {
  if (pathname.startsWith(ROUTES.feed)) return 'feed';
  if (pathname.startsWith(ROUTES.grid)) return 'people';
  if (pathname.startsWith(ROUTES.map)) return 'places';
  if (pathname.startsWith(ROUTES.messages)) return 'messages';
  if (pathname.startsWith(ROUTES.settings)) return 'passport';
  return '';
}

export function HottrChassisNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = location;
  const { messages, send } = useHottieChat();

  useEffect(() => {
    const state = location.state as { openHottieSheet?: boolean } | null;
    if (!state?.openHottieSheet) return;
    // Click first. Replacing state before the click re-ran this effect
    // (location.state is a dep) and the cleanup cancelled the open.
    openInKitHottieSheet();
    navigate(`${location.pathname}${location.search}`, { replace: true, state: {} });
  }, [location.pathname, location.search, location.state, navigate]);

  if (SUPPRESSED.has(pathname) || pathname.startsWith('/auth')) return null;

  const go = (route: string) => {
    const to = PATHS[route];
    if (to) navigate(to);
  };

  return (
    <>
      {/* The one Orb: OrbFan's dock, at every width. There is no
          desktop rail to mount beside the page. */}
      <OrbFan
        product="app"
        activeRoute={activeRouteFor(pathname)}
        onNavigate={go}
        hottieMessages={messages}
        onHottieSend={send}
        orbState="interested"
      />
    </>
  );
}
